const selectors = {
  mediaColumn: '[data-timeline-media-column]',
  step: '[data-timeline-step]',
  media: '[data-timeline-media]',
  mediaHost: '[data-timeline-media-host]',
  panel: '[data-timeline-step-panel]',
  progressFill: '[data-timeline-progress-fill]',
};

const classes = {
  hidden: 'hidden',
  mediaVisible: 'opacity-100',
  mediaHidden: 'opacity-0',
  // The media nodes are stacked on top of each other in the sticky column, so
  // an inactive one must not swallow clicks meant for the visible one. The
  // markup carries pointer-events-none for that reason, which also killed the
  // active video's own play/pause controls until this was toggled with it.
  mediaInert: 'pointer-events-none',
  mediaInteractive: 'pointer-events-auto',
  stepActive: 'opacity-100',
  stepInactive: 'opacity-60',
  panelOpen: 'grid-rows-[1fr]',
  panelClosed: 'grid-rows-[0fr]',
};

export default class DynamicStickyMediaWSteps {
  constructor(section) {
    this.el = section.el;
    this.id = section.id;

    this.activeIndex = -1;

    this.mediaColumn = this.el.querySelector(selectors.mediaColumn);
    this.steps = Array.from(this.el.querySelectorAll(selectors.step));
    this.progressFills = Array.from(
      this.el.querySelectorAll(selectors.progressFill),
    );

    this._init();
  }

  _init() {
    if (!this.mediaColumn) {
      return;
    }

    if (this.steps.length === 0) {
      this.mediaColumn.classList.add(classes.hidden);
      return;
    }

    this._moveMedia();

    this.mediaColumn.id = `${this.id}-timeline-media`;
    this.mediaColumn.setAttribute('aria-hidden', 'true');

    // Set the opening state synchronously. The section is registered lazily,
    // so it can already be partway into view by the time this runs.
    this.setActive(0);

    this.el.addEventListener('click', this.onClick);
  }

  /**
   * Relocate each step's media node into the sticky column. Steps can be
   * nested at any depth (inside a group block, for example), so the nodes are
   * matched by block id rather than by position. Re-running is harmless:
   * appendChild moves a node that is already there without recreating it.
   */
  _moveMedia() {
    this.mediaNodes = this.steps.map(step => {
      const blockId = step.dataset.timelineStepId;
      const media = this.el.querySelector(
        `${selectors.media}[data-timeline-media-for="${blockId}"]`,
      );

      if (!media) {
        return null;
      }

      const host = media.closest(selectors.mediaHost);
      this.mediaColumn.appendChild(media);

      if (host) {
        host.remove();
      }

      return media;
    });
  }

  setActive = index => {
    if (index === this.activeIndex || index < 0 || index >= this.steps.length) {
      return;
    }

    this.activeIndex = index;

    this.mediaNodes.forEach((media, i) => {
      if (!media) {
        return;
      }

      const isActive = i === index;
      media.classList.toggle(classes.mediaVisible, isActive);
      media.classList.toggle(classes.mediaHidden, !isActive);
      media.classList.toggle(classes.mediaInteractive, isActive);
      media.classList.toggle(classes.mediaInert, !isActive);
    });

    this.steps.forEach((step, i) => {
      const isActive = i === index;

      step.classList.toggle(classes.stepActive, isActive);
      step.classList.toggle(classes.stepInactive, !isActive);

      const panel = step.querySelector(selectors.panel);

      if (!panel) {
        step.removeAttribute('aria-expanded');
        return;
      }

      step.setAttribute('aria-expanded', isActive ? 'true' : 'false');

      panel.classList.toggle(classes.panelOpen, isActive);
      panel.classList.toggle(classes.panelClosed, !isActive);

      if (isActive) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
    });

    this.updateProgress();
  };

  /**
   * Fill spans the active step's share of the track, so step 1 of 4 reads as
   * 25% and the last step fills it completely.
   */
  updateProgress = () => {
    const percent = ((this.activeIndex + 1) / this.steps.length) * 100;

    this.progressFills.forEach(fill => {
      fill.style.width = `${percent}%`;
    });
  };

  onClick = event => {
    const step = event.target.closest(selectors.step);

    if (!step || !this.el.contains(step)) {
      return;
    }

    const index = this.steps.indexOf(step);
    if (index !== -1) {
      this.setActive(index);
    }
  };

  onSectionBlockSelect = ({ id }) => {
    const index = this.steps.findIndex(
      step => step.dataset.timelineStepId === id,
    );

    if (index === -1) {
      return;
    }

    this.setActive(index);
  };

  onSectionUnload = () => {
    this.el.removeEventListener('click', this.onClick);
  };
}
